import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Caption1, MessageBar, MessageBarBody, Text, makeStyles, mergeClasses } from '@fluentui/react-components';
import { ShellIcons, shellMedia, shellMetrics, tokens } from '@vigeo/ui';
import {
  fileCategoryLabels, isRendered, offerAcceptByText, offerSendChecks, renderMail, templateFor,
  formatRecordedCalendarDate, type HrWorkflowView, type OfferRecord, type Person,
} from '@vigeo/shared';
import { OfferCard, OfferRow, OfferStageBadge, OfferValues, rateText, reachedStage } from '@/features/offers';
import { OfferTracker } from '../OfferTracker.js';
import { OfferPreviewDialog } from './OfferPreviewDialog.js';
import { DeliveryNotice } from './DeliveryNotice.js';
import { byLabel, useSignedInUserId } from '../actors.js';
import { PortalPreview } from './PortalPreview.js';
import { offerTrackerSteps } from '../model.js';
import { WithdrawOfferButton, performHrWorkflow } from '@/features/hr-workflow';

/**
 * Row 1.4 — frame 06b `297:932`. One button issues the offer and tells the candidate. Drawn
 * inline in Offer and stage (#440) and, through `OfferSendPage`, on its own route.
 *
 * The email shown here is RENDERED, not mocked up: the same `offer-invite` template and the same
 * renderer the server uses, with the link and code stood in for. A hand-written preview would be
 * a second copy of the message to keep in step with the one that actually goes out.
 */

/**
 * What the preview stands in for. None of these exist at preview time.
 *
 * The link and the code are minted by the send. The HR contact is deployment configuration
 * (`HR_CONTACT`), which the server merges in — so the preview must not print a plausible-looking
 * one. It showed an invented phone number under "The offer email they get", on an environment
 * where that setting may not be set at all and the send would be refused for want of it.
 */
const PREVIEW_LINK = 'https://portal.example.invalid/p/preview';
const PREVIEW_CODE = '••••••';
const PREVIEW_HR_CONTACT = 'preview-hr-contact@example.invalid';

const useStyles = makeStyles({
  status: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', minWidth: 0 },
  statusNote: { color: tokens.colorNeutralForeground2 },
  pdf: {
    display: 'flex', flexDirection: 'row', alignItems: 'center', gap: tokens.spacingHorizontalS, minWidth: 0, flexWrap: 'wrap',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium, border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  pdfIcon: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    width: '2rem', height: '2rem', borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground1,
  },
  pdfText: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, flexGrow: 1, flexBasis: '12rem', minWidth: 0 },
  pdfName: { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold, overflowWrap: 'anywhere' },
  link: {
    display: 'inline-flex', flexDirection: 'row', alignItems: 'center', gap: tokens.spacingHorizontalXXS,
    color: tokens.colorBrandForeground1, textDecorationLine: 'none',
    fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold,
    [shellMedia.phone]: { minHeight: shellMetrics.touchTarget },
  },
  mail: {
    display: 'flex', flexDirection: 'column', alignItems: 'stretch', minWidth: 0,
    borderRadius: tokens.borderRadiusMedium, border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`, overflow: 'hidden',
  },
  mailHeaders: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  mailHeader: { display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: tokens.spacingHorizontalS, minWidth: 0 },
  mailKey: { width: '4rem', flexShrink: 0, color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 },
  mailValue: { minWidth: 0, overflowWrap: 'anywhere', fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300 },
  mailSubject: { fontWeight: tokens.fontWeightSemibold },
  mailBody: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL, backgroundColor: tokens.colorNeutralBackground1,
    whiteSpace: 'pre-line', fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400, overflowWrap: 'anywhere',
  },
  /**
   * The blank lines between the email's paragraphs, which were being collapsed.
   *
   * `mailBody` above already sets `pre-line` and the body string does carry the template's `\n\n`
   * — verified in the browser: the span's text contains newlines and its parent computes
   * `pre-line`. But Fluent's `Text` sets `white-space: normal` on ITSELF, and a value set on the
   * element beats one inherited from its parent. So the preview ran the whole letter together as
   * one paragraph while the frame shows five.
   */
  mailText: { whiteSpace: 'pre-line' },
  check: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: tokens.spacingHorizontalXS, minWidth: 0 },
  checkOk: { color: tokens.colorBrandForeground1, display: 'inline-flex', flexShrink: 0 },
  checkBad: { color: tokens.colorStatusDangerForeground1, display: 'inline-flex', flexShrink: 0 },
  when: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: tokens.spacingHorizontalS, minWidth: 0 },
  whenNumber: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    width: '1.5rem', height: '1.5rem', borderRadius: tokens.borderRadiusCircular,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
    color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, fontWeight: tokens.fontWeightSemibold,
  },
  otherWays: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, flexGrow: 1, flexBasis: '16rem', minWidth: 0 },
  otherWay: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: tokens.spacingHorizontalXXS, flexWrap: 'wrap' },
  linkButton: { background: 'none', border: 'none', padding: 0, margin: 0, font: 'inherit', cursor: 'pointer' },
  inlineLink: {
    color: tokens.colorBrandForeground1, fontWeight: tokens.fontWeightSemibold, textDecorationLine: 'none',
    fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200,
    display: 'inline-flex', alignItems: 'center',
    [shellMedia.phone]: { minHeight: shellMetrics.touchTarget },
  },
});

export function SendOffer({ person, record, workflow, canEdit, onSent, onRecordPaper, onEdit }: {
  readonly person: Person;
  readonly record: OfferRecord;
  readonly workflow: HrWorkflowView;
  readonly canEdit: boolean;
  /**
   * Hands the delivery result to the caller. Inline, sending moves the offer to "sent", so this
   * screen is replaced before its own confirmation can be read — the caller keeps the message.
   */
  readonly onSent?: (delivery: NonNullable<HrWorkflowView['offerDelivery']>) => void;
  /** Opens the paper-signed form in place. Absent on the standalone route, where it is a link back. */
  readonly onRecordPaper?: () => void;
  /** Opens the editor on this offer. Saving returns it to draft. Absent on the standalone route, where it is a link back. */
  readonly onEdit?: () => void;
}) {
  const styles = useStyles();
  const me = useSignedInUserId();
  const id = person.id;
  const recordId = record.id;
  const queryClient = useQueryClient();
  const [failure, setFailure] = useState<string | null>(null);
  const [viewing, setViewing] = useState<'pdf' | 'portal' | null>(null);
  const viewOpener = useRef<HTMLElement | null>(null);
  const [sent, setSent] = useState<{ delivered: boolean; maskedTo: string | null; reason: string | null } | null>(null);

  const send = useMutation({
    mutationFn: async () => {
      return performHrWorkflow(id, { operationId: crypto.randomUUID(), action: 'issue-offer', recordId, expectedRevision: record.revision });
    },
    onSuccess: async (view) => {
      setSent(view.offerDelivery);
      if (view.offerDelivery !== null) onSent?.(view.offerDelivery);
      await queryClient.invalidateQueries({ queryKey: ['hr-workflow', id] });
    },
    onError: (error: Error) => setFailure(error.message),
  });

  /** The frame's other footer action. `withdraw-offer` is the command the person page already uses. */
  const withdraw = useMutation({
    mutationFn: async () => {
      return performHrWorkflow(id, { operationId: crypto.randomUUID(), action: 'withdraw-offer', recordId, expectedRevision: record.revision });
    },
    onSuccess: async () => {
      setSent(null);
      await queryClient.invalidateQueries({ queryKey: ['hr-workflow', id] });
    },
    onError: (error: Error) => setFailure(error.message),
  });

  const first = person.name.preferredName || person.name.givenName;
  const signed = workflow.offerSteps.some((s) => s.id === record.instanceId && s.state === 'approved');
  const reached = reachedStage(record.status, signed);
  const acceptBy = offerAcceptByText(record.terms.acceptanceDeadline);

  // The same rule the handler refuses on. See `offerSendChecks`: the label column's promise that
  // these run again on Send offer is only true because both read this one function.
  const checks = offerSendChecks({
    offer: record,
    records: workflow.records,
    steps: workflow.offerSteps,
    firstName: first,
    hasEmailOnFile: person.contact.email !== '',
    now: new Date().toISOString(),
  });
  const blocked = checks.some((check) => !check.ok);
  const past = !checks.find((check) => check.id === 'deadline-ahead')?.ok;

  const rendered = renderMail(templateFor('offer-invite'), {
    firstName: first,
    portalLink: PREVIEW_LINK,
    accessCode: 'ABC234',
    hrContact: PREVIEW_HR_CONTACT,
    position: record.terms.position,
    acceptBy,
  });
  // The rendered text with the stand-ins put back, so the preview never shows a made-up code.
  const body = isRendered(rendered)
    ? rendered.text.replace(`Review and sign your offer letter: ${PREVIEW_LINK}`, 'Review and sign your offer letter')
      .replace('Access code: ABC234', `Access code: ${PREVIEW_CODE}`)
      .replace(PREVIEW_HR_CONTACT, 'your agency’s HR contact')
    : null;

  return (
    <OfferCard
      crumb={{ label: 'Offer and stage', href: `/app/people/${encodeURIComponent(id)}?section=hiring`, here: 'Send offer' }}
      title="Send offer"
      lede={`One step: the offer letter goes into ${first}’s portal, and ${first} gets an email with the link and access code.`}
      footer={<>
        {/* Both of these change the record — one issues a portal link, the other records a
            signed offer — so a Viewer is offered neither (gate 6). */}
        {canEdit ? <div className={styles.otherWays}>
          <span className={styles.otherWay}>
            <Caption1>Can’t email {first}?</Caption1>
            <Link to={`/app/people/${encodeURIComponent(id)}?section=documents`} className={styles.inlineLink}>Share the link by hand</Link>
          </span>
          <span className={styles.otherWay}>
            <Caption1>Signed on paper?</Caption1>
            {onRecordPaper === undefined
              ? <Link to={`/app/people/${encodeURIComponent(id)}?section=hiring&paper=offer`} className={styles.inlineLink}>Record the signed copy</Link>
              : <button type="button" className={mergeClasses(styles.inlineLink, styles.linkButton)} onClick={onRecordPaper}>Record the signed copy</button>}
          </span>
        </div> : null}
        {canEdit && record.status === 'reviewed' && onEdit !== undefined
          ? <Button disabled={send.isPending || withdraw.isPending} onClick={onEdit}>Edit offer</Button> : null}
        {canEdit && record.status === 'reviewed' && onEdit === undefined
          ? <Link to={`/app/people/${encodeURIComponent(id)}?section=hiring&draft=offer&record=${encodeURIComponent(record.id)}`} className={styles.link}>Edit offer</Link> : null}
        {canEdit ? <WithdrawOfferButton
          appearance="secondary"
          candidateName={`${person.name.givenName} ${person.name.familyName}`}
          label="Withdraw"
          pendingLabel="Withdrawing…"
          pending={withdraw.isPending}
          disabled={record.status === 'withdrawn' || send.isPending}
          onConfirm={() => { setFailure(null); withdraw.mutate(); }}
        /> : null}
        {canEdit ? <Button
          appearance="primary"
          disabled={record.status !== 'reviewed' || blocked || send.isPending || withdraw.isPending}
          onClick={() => { setFailure(null); send.mutate(); }}
        >
          {send.isPending ? 'Sending…' : 'Send offer'}
        </Button> : null}
      </>}
    >
      {failure === null ? null : (
        <OfferRow label="Not sent"><MessageBar intent="error"><MessageBarBody>{failure}</MessageBarBody></MessageBar></OfferRow>
      )}
      {/* Inline, the caller keeps the result: this screen is replaced the moment the offer is sent. */}
      {sent === null || onSent !== undefined ? null : (
        <OfferRow label={sent.delivered ? 'Sent' : 'Issued, not emailed'}>
          <DeliveryNotice person={person} delivery={sent} canEdit={canEdit} />
        </OfferRow>
      )}

      <OfferRow label="Offer" hint="Reviewed. Editing it returns it to draft, to be reviewed again.">
        <div className={styles.status}>
          <OfferStageBadge reached={reached} past={past} />
          {record.reviewedAt === null ? null : (
            <Caption1 className={styles.statusNote}>
              Reviewed {offerAcceptByText(record.reviewedAt)}{byLabel(record.reviewedBy, me)}
            </Caption1>
          )}
        </div>
        {/* tickReached: frame 297:932 ticks Draft AND Reviewed — the review is done and
            "Sent" is what is next. See OfferTracker for why this is a prop and not a rewrite. */}
        <OfferTracker steps={offerTrackerSteps} reached={reached} label="Offer status" tickReached />
        <OfferValues items={[
          { label: 'Position', value: record.terms.position },
          { label: 'Classification', value: `${record.terms.employmentClassification === 'w2' ? 'W-2' : '1099'} · ${record.terms.employmentType}` },
          { label: 'Proposed start', value: formatRecordedCalendarDate(record.terms.proposedStartDate) },
        ]} />
        <OfferValues items={[
          { label: 'Rates', value: record.terms.compensation.map(rateText).join('\n') },
          { label: 'Accept by', value: acceptBy },
          { label: 'Signed copy filed to', value: fileCategoryLabels[record.terms.target] },
        ]} />
        <div className={styles.pdf}>
          <span className={styles.pdfIcon} aria-hidden="true"><ShellIcons.document /></span>
          <span className={styles.pdfText}>
            <span className={styles.pdfName}>Offer letter – {record.candidateName}.pdf</span>
            <Caption1>PDF · prepared from the reviewed terms</Caption1>
          </span>
          <button type="button" className={mergeClasses(styles.link, styles.linkButton)} onClick={(event) => { viewOpener.current = event.currentTarget; setViewing('pdf'); }}>
            View PDF<ShellIcons.externalLink />
          </button>
        </div>
      </OfferRow>

      <OfferRow label="Sends to" hint={`From ${first}’s record. Change the email in their profile.`}>
        <OfferValues items={[
          { label: 'Email', value: person.contact.email || 'None on file' },
          { label: 'Portal link', value: 'Lasts 14 days' },
          { label: 'Access code', value: 'Created when you send' },
        ]} />
        <button type="button" className={mergeClasses(styles.link, styles.linkButton)} onClick={(event) => { viewOpener.current = event.currentTarget; setViewing('portal'); }}>
          See what {first} sees in the portal<ShellIcons.chevronRight />
        </button>
      </OfferRow>

      <OfferRow label="The email" hint="The offer email they get, in the same plain HTML as the app’s other emails. No attachment.">
        <div className={styles.mail}>
          <div className={styles.mailHeaders}>
            <div className={styles.mailHeader}><span className={styles.mailKey}>From</span><span className={styles.mailValue}>Vigeo Home Health HR</span></div>
            <div className={styles.mailHeader}><span className={styles.mailKey}>To</span><span className={styles.mailValue}>{person.contact.email || 'None on file'}</span></div>
            <div className={styles.mailHeader}>
              <span className={styles.mailKey}>Subject</span>
              {/* mergeClasses: a template literal here is truncated at the second sequence hash,
                  which would silently drop the subject's weight. */}
              <span className={mergeClasses(styles.mailValue, styles.mailSubject)}>{isRendered(rendered) ? rendered.subject : 'Your offer'}</span>
            </div>
          </div>
          <div className={styles.mailBody}>
            {body === null
              ? <Text>This email could not be prepared. Contact support before sending.</Text>
              : <Text className={styles.mailText}>{body}</Text>}
          </div>
        </div>
        <Caption1>The offer email, filled in for {first}. The access code is created when you send.</Caption1>
      </OfferRow>

      <OfferRow label="Before you send" hint="Checked again when you press Send offer.">
        {checks.map((check) => (
          <span key={check.id} className={styles.check}>
            <span className={check.ok ? styles.checkOk : styles.checkBad} aria-hidden="true">
              {check.ok ? <ShellIcons.check /> : <ShellIcons.close />}
            </span>
            {/* The statement, then the server's own refusal when it is the one blocking. */}
            <Text>{check.ok ? check.label : check.refusal}</Text>
          </span>
        ))}
      </OfferRow>

      <OfferRow label="What happens when you send">
        {[
          `The offer letter appears in ${first}’s portal as “Review and sign your offer”.`,
          `${first} gets the email above, with the portal link and access code. The PDF isn’t attached.`,
          'The offer shows as Sent until they sign. The portal shows when they open the link.',
        ].map((text, index) => (
          <span key={text} className={styles.when}>
            <span className={styles.whenNumber} aria-hidden="true">{index + 1}</span>
            <Text>{text}</Text>
          </span>
        ))}
      </OfferRow>
      <OfferPreviewDialog open={viewing === 'pdf'} opener={viewOpener} title={`Offer letter – ${record.candidateName}`} onClose={() => setViewing(null)}>
        {viewing === 'pdf' ? <PortalPreview person={person} record={record} letterOnly /> : null}
      </OfferPreviewDialog>
      <OfferPreviewDialog open={viewing === 'portal'} opener={viewOpener} title={`What ${first} sees in the portal`} onClose={() => setViewing(null)}>
        {viewing === 'portal' ? <PortalPreview person={person} record={record} /> : null}
      </OfferPreviewDialog>
    </OfferCard>
  );
}
